import { Colors, type TreeSelectNode } from "@snapchat/snap-design-system";

import {
    type AudienceCategory,
    type InsightsDeviceSpecJson,
    type InsightsOsType,
    type InsightsTargetingSpecJson,
} from "../../gql-generated/insights-types";
import { getArrayFirstItems } from "../../lens-insights/filters";
import { type AgeGenderMetrics, type CountriesStatsItem, type OsStatsItem } from "../../lens-insights/models";
import { countryFullNameMap } from "../../lens-insights/models/label-maps";
import { type GetAudiencePlannerQuery } from "../api/queries";
import { type InsightsParsedData, type LensStatsItem } from "../types";

const colorsBreakdown = [Colors.Black.V50, Colors.Gray.V300, Colors.Gray.V250, Colors.Gray.V200, Colors.Gray.V150];
const languageName = new Intl.DisplayNames(["en"], {
    type: "language",
});

export const languagePrefix = "lang_";
export const osPrefix = "os_";
export const countryPrefix = "country_";

export const parseDemographicOverviewData = (data: GetAudiencePlannerQuery): InsightsParsedData => {
    const potentialAudience = [
        data.getAudiencePlanner?.data?.targetAudience?.audienceSizeMinimum ?? 0,
        data.getAudiencePlanner?.data?.targetAudience?.audienceSizeMaximum ?? 0,
    ] as const;

    let totalPercentageFemale = 0;
    let totalPercentageMale = 0;

    const osData: OsStatsItem[] = [];

    let averageAge = 0;

    const countryStats: CountriesStatsItem[] = [];
    const languageStats: Record<string, CountriesStatsItem> = {};
    const deviceStats: CountriesStatsItem[] = [];
    const osVersionStats: CountriesStatsItem[] = [];
    const lensStats: LensStatsItem[] = [];

    const ageGroupStats: Record<string, { men: number; women: number }> = {};
    data.getAudiencePlanner?.data?.categoriesMap
        ?.filter((dimension: AudienceCategory | null): dimension is AudienceCategory => !!dimension)
        .forEach((dimension: AudienceCategory) => {
            // parses gender percentage
            if (dimension.dimensionName === "gender" && dimension.id === "FEMALE") {
                totalPercentageFemale = dimension.targetAudiencePercent ?? 0;
            } else if (dimension.dimensionName === "gender" && dimension.id === "MALE") {
                totalPercentageMale = dimension.targetAudiencePercent ?? 0;
            }

            // parses age group stats
            if (dimension.dimensionName === "age_groups_gender") {
                if (dimension.id?.includes("FEMALE_")) {
                    const key = dimension.id.replace("FEMALE_", "");
                    ageGroupStats[key] = {
                        women: dimension.targetAudiencePercent ?? 0,
                        men: ageGroupStats[key]?.men ?? 0,
                    };
                } else if (dimension.id?.includes("MALE_")) {
                    const key = dimension.id.replace("MALE_", "");
                    ageGroupStats[key] = {
                        men: dimension.targetAudiencePercent ?? 0,
                        women: ageGroupStats[key]?.women ?? 0,
                    };
                }
            }

            // parse age distribution
            if (dimension.dimensionName === "age" && Number.parseInt(dimension.id ?? "") < 65) {
                // NOTE: ages over 65 are currently excluded
                averageAge += (Number.parseInt(dimension.id ?? "") * (dimension.targetAudiencePercent ?? 0)) / 100;
            }

            // parse os data
            if (dimension.dimensionName === "os_type") {
                osData.push({
                    dimensionBreakdown: dimension.name ?? "",
                    value: dimension.targetAudiencePercent ?? 0,
                    valuePercent: dimension.targetAudiencePercent ?? 0,
                    label: dimension.id ?? "",
                });
            }

            // parse country data
            if (dimension.dimensionName === "country_code") {
                countryStats.push({
                    key: countryFullNameMap[`COUNTRY_${dimension.id}`] ?? "",
                    value: dimension.targetAudiencePercent ?? 0,
                    pct: dimension.targetAudiencePercent?.toString() ?? "",
                    width: 0,
                });
            }

            // parse language data
            if (dimension.dimensionName === "languages" && dimension.id) {
                languageStats[dimension.id] = {
                    key: languageName.of(dimension.id) ?? "",
                    value: dimension.targetAudiencePercent ?? 0,
                    pct: dimension.targetAudiencePercent?.toString() ?? "",
                    width: 0,
                };
            }

            // parse device data
            if (dimension.dimensionName === "marketing_name_only") {
                deviceStats.push({
                    key: dimension.id ?? "",
                    value: dimension.targetAudiencePercent ?? 0,
                    pct: dimension.targetAudiencePercent?.toString() ?? "",
                    width: 0,
                });
            }

            // parse os version data
            if (dimension.dimensionName === "os_version") {
                // All Android versions have an id lesser than this value, all iOS ids are greater or equal
                const prefix = dimension.id && dimension.id < "3383214458535936" ? "Android " : "iOS ";
                osVersionStats.push({
                    key: prefix + dimension.name,
                    value: dimension.targetAudiencePercent ?? 0,
                    pct: dimension.targetAudiencePercent?.toString() ?? "",
                    width: 0,
                });
            }

            // parse lens data
            if (dimension.dimensionName === "top_lens_used" && dimension.id && dimension.id.length > 1) {
                lensStats.push({
                    id: dimension.id,
                    name: dimension.name ?? "",
                    pct: dimension.targetAudiencePercent?.toString() ?? "",
                });
            }
        });

    const maleAudience = totalPercentageMale;
    const femaleAudience = totalPercentageFemale;

    const countryStatsWithWidths = setHorizontalBarWidths(countryStats);
    const languageStatsWithWidths = setHorizontalBarWidths(Object.values(languageStats));
    const deviceStatsWithWidths = setHorizontalBarWidths(deviceStats);
    const osVersionStatsWithWidths = setHorizontalBarWidths(osVersionStats);

    const ageGenderMetrics: AgeGenderMetrics = {
        stats: Object.entries(ageGroupStats)
            .map(([key, { men, women }]) => ({
                dimensionBreakdown: key,
                men: men,
                women: women,
            }))
            .filter((entry) => entry.dimensionBreakdown !== "UNKNOWN"),
        womenPct: femaleAudience.toString(),
        menPct: maleAudience.toString(),
        isEmpty: false,
    };

    const osDataWithColor = osData.map((entry, index) => {
        return {
            ...entry,
            color: colorsBreakdown[index],
        };
    });

    // sort lenses and get top 10
    const topTenLensStats = lensStats.sort((a, b) => (a.pct > b.pct ? -1 : 1)).slice(0, 10);

    return {
        demographicsData: {
            potentialAudience,
            maleAudience,
            femaleAudience,
            averageAge,
        },
        ageGenderMetrics,
        osData: osDataWithColor,
        countryStats: countryStatsWithWidths,
        languageStats: languageStatsWithWidths,
        deviceStats: deviceStatsWithWidths,
        osVersionData: osVersionStatsWithWidths,
        lensStats: topTenLensStats,
    };
};

const setHorizontalBarWidths = (stats: CountriesStatsItem[]): CountriesStatsItem[] => {
    let sorted: CountriesStatsItem[] = stats.sort((a: CountriesStatsItem, b: CountriesStatsItem) => {
        return a.value > b.value ? -1 : 1;
    });

    sorted = getArrayFirstItems(sorted, 10);

    if (sorted[0]) {
        sorted[0].width = 100;
    }
    sorted.slice(1).forEach((entry: CountriesStatsItem, index: number) => {
        const field = sorted[index];
        entry.width = field ? (entry.value / field.value) * field.width : 0;
    });

    return sorted;
};

export const getAllDevices = (data: GetAudiencePlannerQuery): TreeSelectNode[] => {
    const deviceMap: Record<string, TreeSelectNode> = {};
    const dimensions = data.getAudiencePlanner?.data?.categoriesMap;
    dimensions
        ?.filter((dimension: AudienceCategory | null): dimension is AudienceCategory => !!dimension)
        .forEach((dimension: AudienceCategory) => {
            if (dimension.dimensionName === "marketing_name_only") {
                const make = capitalizeFirstLetter(dimension.id?.slice(0, dimension.id.indexOf("/")) ?? "");
                const name = capitalizeFirstLetter(dimension.name ?? "");
                if (Object.keys(deviceMap).includes(make)) {
                    deviceMap[make]?.children?.push({
                        key: dimension.id ?? "",
                        value: dimension.id ?? undefined,
                        title: name,
                        checkable: true,
                    });
                } else {
                    deviceMap[make] = {
                        key: make,
                        value: dimension.id ?? undefined,
                        title: make,
                        checkable: true,
                        children: [
                            {
                                key: dimension.id ?? "",
                                value: name,
                                title: name,
                                checkable: true,
                            },
                        ],
                    };
                }
            }
        });

    return Object.values(deviceMap).sort((a, b) => (a.key > b.key ? 1 : -1));
};

export const getAllLanguages = (data: GetAudiencePlannerQuery): TreeSelectNode[] => {
    const dimensions = data.getAudiencePlanner?.data?.categoriesMap;
    const languages: TreeSelectNode[] = [];
    dimensions
        ?.filter(
            (dimension: AudienceCategory | null): dimension is AudienceCategory =>
                dimension?.dimensionName === "languages",
        )
        .forEach((dimension: AudienceCategory) => {
            if (!languages.some((node) => node.key === dimension.id)) {
                languages.push({
                    key: languagePrefix + dimension.id,
                    value: languageName.of(dimension.id ?? ""),
                    title: languageName.of(dimension.id ?? ""),
                    checkable: true,
                });
            }
        });
    return languages.sort((a, b) => (a.key > b.key ? 1 : -1));
};

export const getAllOS = (data: GetAudiencePlannerQuery): TreeSelectNode[] => {
    const dimensions = data.getAudiencePlanner?.data?.categoriesMap;
    const osTypes: TreeSelectNode[] =
        dimensions
            ?.filter(
                (dimension: AudienceCategory | null): dimension is AudienceCategory =>
                    dimension?.dimensionName === "os_type",
            )
            .map((dimension: AudienceCategory) => {
                return {
                    key: osPrefix + dimension.id,
                    value: dimension.id ?? undefined,
                    title: dimension.id,
                    checkable: true,
                };
            }) ?? [];
    return osTypes.sort((a, b) => (a.key > b.key ? 1 : -1));
};

export const getAllCountries = (data: GetAudiencePlannerQuery): TreeSelectNode[] => {
    const dimensions = data.getAudiencePlanner?.data?.categoriesMap;
    const countries: TreeSelectNode[] =
        dimensions
            ?.filter(
                (dimension: AudienceCategory | null): dimension is AudienceCategory =>
                    dimension?.dimensionName === "country_code",
            )
            .map((dimension: AudienceCategory) => {
                return {
                    key: countryPrefix + dimension.id,
                    value: dimension.id ?? undefined,
                    title: dimension.id,
                    checkable: true,
                };
            }) ?? [];
    return countries.sort((a, b) => (a.key > b.key ? 1 : -1));
};

const capitalizeFirstLetter = (text: string): string => {
    if (text.toLowerCase().includes("iphone") || text.toLowerCase().includes("ipad")) {
        return text.charAt(0) + text.charAt(1).toUpperCase() + text.slice(2).toLowerCase();
    }
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const findExistingSpecByOsType = (
    targetingSpec: InsightsTargetingSpecJson,
    osType: InsightsOsType,
): InsightsDeviceSpecJson | undefined => {
    return targetingSpec.devices?.find((spec) => spec?.os_type === osType) ?? undefined;
};
